'use client';

import { useEffect, useRef } from 'react';

/** Divider that lands exactly ON a grid row line. On md+ it stretches from
 *  its natural position down to the next horizontal grid line (keeping at
 *  least the original 44px gap) and paints its 1px rule precisely over that
 *  line, so the separator reads as part of the grid. Below md (pages don't
 *  follow the grid there) it falls back to plain mt-11 spacing.
 *
 *  Extracted from PricingTiersSection so /pricing and /changelog share one
 *  implementation. */
export default function GridRule({ spacer = false }: {
  /** Paint nothing — just pad down to the next row line so whatever follows
   *  (e.g. the dark footer) starts exactly on a grid row edge. */
  spacer?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const md = window.matchMedia('(min-width: 768px)');
    const update = () => {
      if (!md.matches) {
        el.style.height = '';
        return;
      }
      const cell =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--grid-cell'),
        ) || 72;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const gap = spacer ? 0 : 44;
      const next = Math.ceil((top + gap) / cell) * cell;
      // +1 so the border-bottom pixel covers the grid line's own pixel
      // (lines are drawn at the top edge of each row). A spacer paints
      // nothing, so it stops on the line itself.
      el.style.height = `${next - top + (spacer ? 0 : 1)}px`;
    };
    update();
    // Re-measure when layout can shift: viewport resize (clamp() hero type),
    // web font arrival, and grid re-publishes.
    window.addEventListener('resize', update);
    window.addEventListener('grid-relayout', update);
    md.addEventListener('change', update);
    document.fonts?.ready.then(update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('grid-relayout', update);
      md.removeEventListener('change', update);
    };
  }, [spacer]);

  if (spacer) {
    return <div ref={ref} aria-hidden className="hidden md:block" />;
  }

  // The content column sits half a cell inside the vertical rails (mid-column
  // rule), so the rule extends half a cell each side to start and end exactly
  // ON the vertical grid lines. 1px — the border-line color alone already
  // reads a step stronger than the grid hairlines (2px was too heavy).
  return (
    <div
      ref={ref}
      aria-hidden
      className="mt-11 border-b border-line md:mx-[calc(var(--grid-cell,72px)/-2)] md:mt-0"
    />
  );
}
